import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Plus, Search, MoreVertical, Edit, Trash2, Copy, Play, 
  Zap, RefreshCw, Building2, CheckCircle, XCircle, Clock,
  Target, Phone, MessageCircle, Mail, Users, GitBranch,
  Calendar, FileText, DollarSign, Settings, Eye, History,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Trigger icons
const triggerIcons = {
  contact_created: Users,
  lead_created: Target,
  lead_updated: Edit,
  deal_created: DollarSign,
  deal_stage_changed: GitBranch,
  deal_won: CheckCircle,
  call_completed: Phone,
  whatsapp_received: MessageCircle,
  form_submitted: FileText,
  booking_created: Calendar,
  task_completed: CheckCircle,
  scheduled: Clock
};

// Module colors - light theme
const moduleColors = {
  CRM: 'bg-blue-100 text-blue-700 border-blue-200',
  REAL_ESTATE: 'bg-green-100 text-green-700 border-green-200',
  MARKETING: 'bg-purple-100 text-purple-700 border-purple-200',
  COMMUNICATION: 'bg-amber-100 text-amber-700 border-amber-200',
  OPERATIONS: 'bg-slate-100 text-slate-700 border-slate-200'
};

// Workflow Card Component
const WorkflowCard = ({ workflow, onToggle, onEdit, onDelete, onDuplicate, onViewHistory }) => {
  const TriggerIcon = triggerIcons[workflow.trigger?.type] || Zap;
  
  return (
    <Card className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all" data-testid={`workflow-card-${workflow.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1">
            <div className={`p-2 rounded-lg ${workflow.is_active ? 'bg-green-100' : 'bg-slate-100'}`}>
              <TriggerIcon className={`h-5 w-5 ${workflow.is_active ? 'text-green-600' : 'text-slate-500'}`} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-medium text-slate-900">{workflow.name}</h3>
                {workflow.is_active ? (
                  <Badge className="bg-green-100 text-green-700 border border-green-200">Active</Badge>
                ) : (
                  <Badge variant="secondary" className="bg-slate-100 text-slate-600">Inactive</Badge>
                )}
              </div>
              <p className="text-sm text-slate-600 mt-1 line-clamp-2">
                {workflow.description || 'No description'}
              </p>
              <div className="flex items-center gap-3 mt-2">
                <Badge className={moduleColors[workflow.module_name] || 'bg-slate-100 text-slate-600'}>
                  {workflow.module_name || 'CRM'}
                </Badge>
                <span className="text-xs text-slate-500">
                  Trigger: {workflow.trigger?.type?.replace(/_/g, ' ')}
                </span>
                <span className="text-xs text-slate-500">
                  {workflow.actions?.length || 0} action(s)
                </span>
                {workflow.execution_count > 0 && (
                  <span className="text-xs text-slate-500">
                    Run {workflow.execution_count} time(s)
                  </span>
                )}
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Switch
              checked={workflow.is_active}
              onCheckedChange={(checked) => onToggle(workflow.id, checked)}
              data-testid={`toggle-${workflow.id}`}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-slate-900">
                  <MoreVertical className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="bg-white border-slate-200">
                <DropdownMenuItem onClick={() => onEdit(workflow.id)}>
                  <Edit className="h-4 w-4 mr-2" />
                  Edit
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onDuplicate(workflow.id)}>
                  <Copy className="h-4 w-4 mr-2" />
                  Duplicate
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onViewHistory(workflow.id)}>
                  <History className="h-4 w-4 mr-2" />
                  View History
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onDelete(workflow.id)}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Template Card Component
const TemplateCard = ({ template, onUse }) => {
  const TriggerIcon = triggerIcons[template.trigger?.type] || Zap;
  
  return (
    <Card className="bg-white border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all cursor-pointer" onClick={() => onUse(template)}>
      <CardContent className="p-4">
        <div className="flex items-start gap-3">
          <div className="p-2 rounded-lg bg-blue-100">
            <TriggerIcon className="h-5 w-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-slate-900">{template.name}</h3>
            <p className="text-sm text-slate-600 mt-1">{template.description}</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className={moduleColors[template.module] || 'bg-slate-100 text-slate-600'}>
                {template.module}
              </Badge>
              <span className="text-xs text-slate-500">
                {template.actions?.length || 0} action(s)
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Execution History Dialog
const HistoryDialog = ({ open, onOpenChange, workflowId, token }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    const fetchHistory = async () => {
      if (!open || !workflowId) return;
      
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/workflow-builder/workflows/${workflowId}/history?token=${token}`);
        const data = await res.json();
        if (data.success) {
          setHistory(data.history || []);
        }
      } catch (error) {
        console.error('Failed to fetch history:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchHistory();
  }, [open, workflowId, token]);
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] bg-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-slate-900">
            <History className="h-5 w-5 text-blue-600" />
            Execution History
          </DialogTitle>
          <DialogDescription>
            Recent workflow executions
          </DialogDescription>
        </DialogHeader>
        
        <ScrollArea className="h-[60vh]">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No execution history yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {history.map((exec) => (
                <Card key={exec.id} className="bg-slate-50 border-slate-200">
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {exec.result?.success && exec.result?.conditions_met ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : exec.result?.conditions_met === false ? (
                          <XCircle className="h-4 w-4 text-amber-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-red-500" />
                        )}
                        <span className="text-sm text-slate-900">
                          {exec.result?.conditions_met ? 'Executed' : 'Conditions not met'}
                        </span>
                      </div>
                      <span className="text-xs text-slate-500">
                        {new Date(exec.executed_at).toLocaleString('en-US', { timeZone: 'Asia/Dubai', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </span>
                    </div>
                    <div className="mt-2 text-xs text-slate-600">
                      <p>Actions: {exec.result?.actions_executed?.length || 0}</p>
                      {exec.result?.errors?.length > 0 && (
                        <p className="text-red-600">Errors: {exec.result.errors.join(', ')}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

// Main Workflows List Component
const WorkflowsListNew = () => {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [workflows, setWorkflows] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOrg, setSelectedOrg] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [historyWorkflowId, setHistoryWorkflowId] = useState(null);
  
  const isSuperAdmin = user?.role === 'super_admin';
  
  // Fetch workflows
  const fetchWorkflows = useCallback(async () => {
    try {
      let url = `${API_URL}/api/workflow-builder/workflows?token=${token}`;
      if (selectedOrg !== 'all') {
        url += `&organization_id=${selectedOrg}`;
      }
      if (statusFilter !== 'all') {
        url += `&is_active=${statusFilter === 'active'}`;
      }
      
      const res = await fetch(url);
      const data = await res.json();
      
      if (data.success) {
        setWorkflows(data.workflows || []);
      }
    } catch (error) {
      console.error('Failed to fetch workflows:', error);
      toast.error('Failed to load workflows');
    }
  }, [token, selectedOrg, statusFilter]);
  
  // Fetch data
  useEffect(() => {
    const fetchData = () => {
      setLoading(true);
      // Progressive — templates / orgs / workflows each paint as they arrive.
      const tasks = [
        fetch(`${API_URL}/api/workflow-builder/templates?token=${token}`)
          .then((r) => r.json())
          .then((d) => d?.success && setTemplates(d.templates || []))
          .catch((e) => console.error('[WorkflowsList] templates failed:', e)),
        fetch(`${API_URL}/api/workflow-builder/organizations?token=${token}`)
          .then((r) => r.json())
          .then((d) => d?.success && setOrganizations(d.organizations || []))
          .catch((e) => console.error('[WorkflowsList] orgs failed:', e)),
        Promise.resolve(fetchWorkflows()).catch((e) => console.error('[WorkflowsList] workflows failed:', e)),
      ];
      let done = false;
      const off = () => { if (!done) { done = true; setLoading(false); } };
      const safety = setTimeout(off, 3000);
      Promise.race(tasks).then(off);
      Promise.allSettled(tasks).finally(() => clearTimeout(safety));
    };
    
    fetchData();
  }, [token, fetchWorkflows]);
  
  // Reload on filter change
  useEffect(() => {
    if (!loading) {
      fetchWorkflows();
    }
  }, [selectedOrg, statusFilter, fetchWorkflows, loading]);
  
  // Toggle workflow
  const handleToggle = async (workflowId, isActive) => {
    try {
      const res = await fetch(`${API_URL}/api/workflow-builder/workflows/${workflowId}/toggle?token=${token}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: isActive })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success(isActive ? 'Workflow activated' : 'Workflow deactivated');
        fetchWorkflows();
      } else {
        toast.error(data.error || 'Failed to toggle workflow');
        if (data.missing_integrations) {
          toast.error(`Missing integrations: ${data.missing_integrations.map(m => m.integration).join(', ')}`);
        }
      }
    } catch (error) {
      toast.error('Failed to toggle workflow');
    }
  };
  
  // Delete workflow
  const handleDelete = async (workflowId) => {
    if (!confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/workflow-builder/workflows/${workflowId}?token=${token}`, {
        method: 'DELETE'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Workflow deleted');
        fetchWorkflows();
      } else {
        toast.error(data.error || 'Failed to delete workflow');
      }
    } catch (error) {
      toast.error('Failed to delete workflow');
    }
  };
  
  // Duplicate workflow
  const handleDuplicate = async (workflowId) => {
    try {
      const res = await fetch(`${API_URL}/api/workflow-builder/workflows/${workflowId}/duplicate?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Workflow duplicated');
        fetchWorkflows();
      } else {
        toast.error(data.error || 'Failed to duplicate workflow');
      }
    } catch (error) {
      toast.error('Failed to duplicate workflow');
    }
  };
  
  // Use template
  const handleUseTemplate = (template) => {
    // Navigate to builder with template data
    navigate('/workflows-builder/new', { 
      state: { template } 
    });
  };
  
  // Filter workflows by search
  const filteredWorkflows = workflows.filter(w => 
    w.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    w.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-50 p-6" data-testid="workflows-list">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Zap className="h-6 w-6 text-amber-500" />
              Workflow Automation
            </h1>
            <p className="text-slate-600 mt-1">
              Create automated workflows to streamline your sales process
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button 
              variant="outline"
              onClick={() => navigate('/workflows-builder/analytics')}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="view-analytics-btn"
            >
              <BarChart3 className="h-4 w-4 mr-2" />
              Analytics
            </Button>
            <Button 
              variant="outline"
              onClick={() => navigate('/workflows-builder/history')}
              className="border-slate-200 text-slate-700 hover:bg-slate-100"
              data-testid="view-history-btn"
            >
              <History className="h-4 w-4 mr-2" />
              History
            </Button>
            <Button 
              onClick={() => navigate('/workflows-builder/new')}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="create-workflow-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Workflow
            </Button>
          </div>
        </div>
        
        <Tabs defaultValue="workflows">
          <TabsList className="mb-6 bg-white border border-slate-200">
            <TabsTrigger value="workflows" data-testid="tab-workflows">
              My Workflows
            </TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates">
              Templates
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="workflows">
            {/* Filters */}
            <div className="flex items-center gap-4 mb-6">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search workflows..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 bg-white border-slate-200"
                  data-testid="search-workflows"
                />
              </div>
              
              {isSuperAdmin && (
                <Select value={selectedOrg} onValueChange={setSelectedOrg}>
                  <SelectTrigger className="w-[200px] bg-white border-slate-200">
                    <Building2 className="h-4 w-4 mr-2 text-slate-500" />
                    <SelectValue placeholder="All Organizations" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-slate-200">
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map(org => (
                      <SelectItem key={org.id} value={org.id}>
                        {org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px] bg-white border-slate-200">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent className="bg-white border-slate-200">
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
              
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchWorkflows}
                className="border-slate-200 text-slate-600 hover:bg-slate-100"
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            
            {/* Workflows List */}
            {filteredWorkflows.length === 0 ? (
              <Card className="bg-white border-slate-200">
                <CardContent className="p-12 text-center">
                  <Zap className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-700">No Workflows Yet</h3>
                  <p className="text-sm text-slate-500 mt-1 mb-4">
                    Create your first workflow to automate your sales process
                  </p>
                  <Button onClick={() => navigate('/workflows-builder/new')} className="bg-blue-600 hover:bg-blue-700 text-white">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Workflow
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredWorkflows.map(workflow => (
                  <WorkflowCard
                    key={workflow.id}
                    workflow={workflow}
                    onToggle={handleToggle}
                    onEdit={(id) => navigate(`/workflows-builder/${id}`)}
                    onDelete={handleDelete}
                    onDuplicate={handleDuplicate}
                    onViewHistory={setHistoryWorkflowId}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="templates">
            <div className="grid md:grid-cols-2 gap-4">
              {templates.map(template => (
                <TemplateCard
                  key={template.id}
                  template={template}
                  onUse={handleUseTemplate}
                />
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* History Dialog */}
      <HistoryDialog
        open={!!historyWorkflowId}
        onOpenChange={(open) => !open && setHistoryWorkflowId(null)}
        workflowId={historyWorkflowId}
        token={token}
      />
    </div>
  );
};

export default WorkflowsListNew;
